import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Upload, X, Smile, Film, Loader2 } from 'lucide-react';
import { API, authFetch } from '@/App';
import { toast } from 'sonner';

const EMOJI_LIST = ['✨', '💄', '💋', '🌸', '💕', '🔥', '💅', '👸', '🌟', '💗', '😍', '🥰', '💜', '🌺', '💫', '✌️', '💁‍♀️', '🧴', '🪞', '💆‍♀️'];
const MAX_VIDEO_DURATION = 40; // seconds

export default function CreatePostPage() {
  const navigate = useNavigate();
  const [mediaItems, setMediaItems] = useState([]); // {file, preview, caption}
  const [videoFile, setVideoFile] = useState(null);
  const [videoPreview, setVideoPreview] = useState('');
  const [videoUploading, setVideoUploading] = useState(false);
  const [checklists, setChecklists] = useState([]);
  const [selectedChecklist, setSelectedChecklist] = useState('');
  const [pinnedChecklistId, setPinnedChecklistId] = useState(null);
  const [usePinnedChecklist, setUsePinnedChecklist] = useState(true);
  const [loading, setLoading] = useState(false);
  const [user, setUser] = useState(null);
  const [showEmojiPicker, setShowEmojiPicker] = useState(null);

  useEffect(() => {
    fetchUser();
    fetchChecklists();
  }, []);

  const fetchUser = async () => {
    try {
      const response = await authFetch(`${API}/auth/me`);
      if (response.ok) {
        const userData = await response.json();
        setUser(userData);
        setPinnedChecklistId(userData.pinned_checklist_id);
        if (userData.pinned_checklist_id) {
          setSelectedChecklist(userData.pinned_checklist_id);
        }
      }
    } catch (error) {
      console.error('Error fetching user:', error);
    }
  };

  const fetchChecklists = async () => {
    try {
      const response = await authFetch(`${API}/checklists`);
      if (response.ok) {
        const data = await response.json();
        setChecklists(data);
      }
    } catch (error) {
      console.error('Error fetching checklists:', error);
    }
  };

  const handleFileChange = (e) => {
    const selectedFiles = Array.from(e.target.files || []);
    const totalFiles = mediaItems.length + selectedFiles.length;
    
    if (totalFiles > 10) {
      toast.error('Maximum 10 images allowed');
      return;
    }
    
    selectedFiles.forEach(file => {
      const reader = new FileReader();
      reader.onloadend = () => {
        setMediaItems(prev => [...prev, {
          file,
          preview: reader.result,
          caption: ''
        }]);
      };
      reader.readAsDataURL(file);
    });
  };

  const removeMedia = (index) => {
    setMediaItems(mediaItems.filter((_, i) => i !== index));
  };

  const updateCaption = (index, caption) => {
    setMediaItems(prev => prev.map((item, i) => 
      i === index ? { ...item, caption } : item
    ));
  };

  const addEmoji = (index, emoji) => {
    setMediaItems(prev => prev.map((item, i) => 
      i === index ? { ...item, caption: item.caption + emoji } : item
    ));
    setShowEmojiPicker(null);
  };

  const handleVideoChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('video/')) {
      toast.error('Please select a video file');
      return;
    }
    if (file.size > 50 * 1024 * 1024) {
      toast.error('Video must be under 50MB');
      return;
    }

    // Validate duration
    const video = document.createElement('video');
    video.preload = 'metadata';
    video.onloadedmetadata = () => {
      URL.revokeObjectURL(video.src);
      if (video.duration > MAX_VIDEO_DURATION) {
        toast.error(`Video must be ${MAX_VIDEO_DURATION} seconds or less (yours: ${Math.round(video.duration)}s)`);
        return;
      }
      setVideoFile(file);
      setVideoPreview(URL.createObjectURL(file));
    };
    video.src = URL.createObjectURL(file);
  };

  const removeVideo = () => {
    if (videoPreview) URL.revokeObjectURL(videoPreview);
    setVideoFile(null);
    setVideoPreview('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (mediaItems.length === 0 && !videoFile) {
      toast.error('Please select at least one image or video');
      return;
    }

    const checklistToUse = usePinnedChecklist && pinnedChecklistId ? pinnedChecklistId : selectedChecklist;
    
    if (!checklistToUse) {
      toast.error('Please select a checklist');
      return;
    }

    setLoading(true);
    try {
      // Upload video first if present
      let uploadedVideoUrl = '';
      if (videoFile) {
        setVideoUploading(true);
        const videoFormData = new FormData();
        videoFormData.append('file', videoFile);
        const videoRes = await authFetch(`${API}/upload-media`, {
          method: 'POST',
          body: videoFormData,
        });
        if (!videoRes.ok) {
          const err = await videoRes.json();
          throw new Error(err.detail || 'Video upload failed');
        }
        const videoData = await videoRes.json();
        uploadedVideoUrl = videoData.url;
        setVideoUploading(false);
      }

      const formData = new FormData();
      // If no images but we have video, we still need at least one file for the multipart
      if (mediaItems.length === 0) {
        // Create a tiny placeholder — backend requires files field
        const placeholder = new Blob([new Uint8Array([0])], { type: 'image/png' });
        formData.append('files', placeholder, 'placeholder.png');
      } else {
        mediaItems.forEach(item => {
          formData.append('files', item.file);
        });
      }
      
      const captions = mediaItems.map(item => item.caption);
      formData.append('captions', JSON.stringify(captions));
      formData.append('checklist_id', checklistToUse);
      if (uploadedVideoUrl) {
        formData.append('video_url', uploadedVideoUrl);
      }

      const response = await authFetch(`${API}/posts`, {
        method: 'POST',
        body: formData
      });

      if (response.ok) {
        toast.success('Post created successfully!');
        navigate('/');
      } else {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to create post');
      }
    } catch (error) {
      console.error('Error creating post:', error);
      toast.error(error.message || 'Failed to create post');
    } finally {
      setLoading(false);
      setVideoUploading(false);
    }
  };

  const hasContent = mediaItems.length > 0 || videoFile;
  const totalSlides = (videoFile ? 1 : 0) + mediaItems.length + 1; // video + images + checklist

  return (
    <div className="min-h-screen pb-24">
      {/* Header */}
      <div className="sticky top-0 z-40 bg-background/80 backdrop-blur-xl border-b border-border">
        <div className="max-w-2xl mx-auto px-4 py-4 flex items-center justify-between">
          <button data-testid="back-btn" onClick={() => navigate(-1)} className="p-2 hover:bg-primary/10 rounded-full transition-colors">
            <ArrowLeft className="w-6 h-6" />
          </button>
          <h1 className="text-xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>Create Post</h1>
          <button 
            data-testid="post-submit-btn"
            onClick={handleSubmit} 
            disabled={loading || !hasContent}
            className="btn-primary px-6 py-2 text-sm disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {videoUploading ? 'Uploading...' : loading ? 'Posting...' : 'Post'}
          </button>
        </div>
      </div>

      {/* Form */}
      <div className="max-w-2xl mx-auto px-4 py-6">
        <form onSubmit={handleSubmit} className="space-y-6">
          
          {/* Info Banner */}
          <div className="bg-primary/10 border border-primary/20 rounded-xl p-4">
            <p className="text-sm text-primary font-medium">
              Your post carousel: {videoFile ? 'Video (1)' : ''}{videoFile && mediaItems.length > 0 ? ' + ' : ''}{mediaItems.length > 0 ? `Images (${mediaItems.length})` : ''} + Checklist (1) = <strong>{totalSlides} slides</strong>
            </p>
          </div>

          {/* Video Upload */}
          <div className="space-y-3">
            <label className="block text-sm font-medium text-foreground">
              Video <span className="text-muted-foreground font-normal">(optional, max {MAX_VIDEO_DURATION}s)</span>
            </label>

            {videoFile ? (
              <div className="relative rounded-xl overflow-hidden border border-border bg-black">
                <video src={videoPreview} className="w-full max-h-48 object-contain" controls muted />
                <button
                  data-testid="remove-video"
                  type="button"
                  onClick={removeVideo}
                  className="absolute top-3 right-3 p-2 bg-white/90 rounded-full hover:bg-white transition-colors shadow-lg"
                >
                  <X className="w-4 h-4" />
                </button>
                <div className="absolute top-3 left-3 px-3 py-1.5 bg-black/70 text-white text-xs font-medium rounded-full flex items-center gap-1">
                  <Film className="w-3 h-3" /> Video · Slide 1
                </div>
              </div>
            ) : (
              <label className="flex items-center justify-center w-full h-20 border-2 border-dashed border-border rounded-xl cursor-pointer hover:border-primary transition-colors bg-white/50" data-testid="video-upload-label">
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Film className="w-5 h-5" />
                  <span className="text-sm">Add a video (mp4, mov · max {MAX_VIDEO_DURATION}s)</span>
                </div>
                <input
                  data-testid="video-input"
                  type="file"
                  accept="video/mp4,video/quicktime,video/webm,video/x-m4v"
                  onChange={handleVideoChange}
                  className="hidden"
                />
              </label>
            )}
          </div>

          {/* Media Upload with Per-Image Captions */}
          <div className="space-y-4">
            <label className="block text-sm font-medium text-foreground">
              Photos <span className="text-muted-foreground font-normal">(up to 10 images, each with its own caption)</span>
            </label>
            
            {mediaItems.length > 0 && (
              <div className="space-y-4">
                {mediaItems.map((item, index) => (
                  <div key={index} className="bg-white rounded-xl border border-border overflow-hidden">
                    {/* Image with slide number */}
                    <div className="relative">
                      <img src={item.preview} alt={`Preview ${index + 1}`} className="w-full h-64 object-cover" />
                      <button
                        data-testid={`remove-image-${index}`}
                        type="button"
                        onClick={() => removeMedia(index)}
                        className="absolute top-3 right-3 p-2 bg-white/90 rounded-full hover:bg-white transition-colors shadow-lg"
                      >
                        <X className="w-4 h-4" />
                      </button>
                      <div className="absolute top-3 left-3 px-3 py-1.5 bg-black/70 text-white text-sm font-medium rounded-full">
                        {index + 1}/{totalSlides}
                      </div>
                    </div>
                    
                    {/* Caption input for this image */}
                    <div className="p-3 border-t border-border">
                      <div className="flex items-center gap-2">
                        <input
                          data-testid={`caption-input-${index}`}
                          type="text"
                          value={item.caption}
                          onChange={(e) => updateCaption(index, e.target.value)}
                          placeholder={`Caption for image ${index + 1}...`}
                          className="flex-1 px-3 py-2 rounded-xl border border-border bg-white/50 focus:border-primary outline-none text-sm"
                          maxLength={200}
                        />
                        <div className="relative">
                          <button
                            type="button"
                            onClick={() => setShowEmojiPicker(showEmojiPicker === index ? null : index)}
                            className="p-2 hover:bg-primary/10 rounded-full transition-colors"
                          >
                            <Smile className="w-5 h-5 text-muted-foreground" />
                          </button>
                          
                          {/* Emoji Picker */}
                          {showEmojiPicker === index && (
                            <div className="absolute right-0 top-full mt-2 p-3 bg-white rounded-xl shadow-xl border border-border z-50 w-64">
                              <div className="grid grid-cols-5 gap-2">
                                {EMOJI_LIST.map((emoji, i) => (
                                  <button
                                    key={i}
                                    type="button"
                                    onClick={() => addEmoji(index, emoji)}
                                    className="text-xl hover:bg-gray-100 rounded-lg p-1.5 transition-colors"
                                  >
                                    {emoji}
                                  </button>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                      <p className="text-xs text-muted-foreground mt-1 text-right">
                        {item.caption.length}/200
                      </p>
                    </div>
                  </div>
                ))}
                
                {/* Add more images button */}
                {mediaItems.length < 10 && (
                  <label className="flex items-center justify-center w-full h-24 border-2 border-dashed border-border rounded-xl cursor-pointer hover:border-primary transition-colors bg-white/50">
                    <div className="text-center">
                      <Upload className="w-6 h-6 text-muted-foreground mx-auto mb-1" />
                      <span className="text-sm text-muted-foreground">Add more ({mediaItems.length}/10)</span>
                    </div>
                    <input 
                      data-testid="file-input-add"
                      type="file" 
                      accept="image/*" 
                      multiple
                      onChange={handleFileChange} 
                      className="hidden" 
                    />
                  </label>
                )}
              </div>
            )}
            
            {mediaItems.length === 0 && (
              <label className="flex flex-col items-center justify-center w-full h-40 border-2 border-dashed border-border rounded-2xl cursor-pointer hover:border-primary transition-colors bg-white/50">
                <Upload className="w-10 h-10 text-muted-foreground mb-2" />
                <span className="text-muted-foreground font-medium text-sm">Click to upload images</span>
                <span className="text-xs text-muted-foreground mt-1">Up to 10 images</span>
                <input 
                  data-testid="file-input"
                  type="file" 
                  accept="image/*"
                  multiple
                  onChange={handleFileChange} 
                  className="hidden" 
                />
              </label>
            )}
          </div>

          {/* Checklist Preview - Shows as last slide */}
          {mediaItems.length > 0 && (
            <div className="bg-gradient-to-br from-primary/5 to-accent/5 rounded-xl border border-primary/20 p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-primary">Last Slide: Your Checklist</span>
                <span className="px-2 py-1 bg-primary/20 text-primary text-xs font-medium rounded-full">
                  {totalSlides}/{totalSlides}
                </span>
              </div>
              <p className="text-xs text-muted-foreground">
                Your selected checklist will appear as the final swipable slide
              </p>
            </div>
          )}

          {/* Checklist Selection */}
          <div className="space-y-3">
            <label className="block text-sm font-medium text-foreground">
              Checklist * <span className="text-muted-foreground font-normal">— Appears as final slide</span>
            </label>
            
            {pinnedChecklistId && (
              <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-xl" data-testid="default-checklist-info">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-emerald-700">
                      Default checklist auto-attached
                    </span>
                  </div>
                  <button
                    type="button"
                    data-testid="change-checklist-btn"
                    onClick={() => setUsePinnedChecklist(!usePinnedChecklist)}
                    className="text-xs text-emerald-600 font-semibold hover:underline"
                  >
                    {usePinnedChecklist ? 'Change' : 'Use default'}
                  </button>
                </div>
              </div>
            )}
            
            {checklists.length === 0 ? (
              <div className="text-sm text-muted-foreground p-4 bg-white/50 rounded-xl border border-border">
                <p>You don't have any checklists yet.</p>
                <button
                  data-testid="create-checklist-link"
                  type="button"
                  onClick={() => navigate('/create-checklist')}
                  className="text-primary hover:underline mt-2"
                >
                  Create your first checklist
                </button>
              </div>
            ) : (!usePinnedChecklist || !pinnedChecklistId) ? (
              <select
                id="checklist"
                data-testid="checklist-select"
                value={selectedChecklist}
                onChange={(e) => {
                  setSelectedChecklist(e.target.value);
                  setUsePinnedChecklist(false);
                }}
                className="w-full px-4 py-3 rounded-xl border-2 border-border bg-white/50 focus:border-primary focus:ring-4 focus:ring-primary/10 transition-all outline-none"
                required
              >
                <option value="">Select a checklist</option>
                {checklists.map(checklist => (
                  <option key={checklist.checklist_id} value={checklist.checklist_id}>
                    {checklist.name} ({checklist.products?.length || 0} products) {checklist.checklist_id === pinnedChecklistId ? '(default)' : ''}
                  </option>
                ))}
              </select>
            ) : null}
          </div>
        </form>
      </div>
    </div>
  );
}
